// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { Navigate, useParams } from "react-router-dom"
import { useAgent } from "@/queries/queries.agents"
import { AgentModelRenderer } from "./agentModelRenderer"
import { AgentHeader } from "./agentHeader"
import { BottomCards, LineageCard } from "./bottomCards"
import { FullPageSpinnerWithText } from "@/components/ui/spinner"

export default function AgentDetail() {
  const { agentId } = useParams()

  const { data, isLoading, isError } = useAgent(agentId)

  if (!agentId) {
    return <Navigate to="/" />
  }

  if (isLoading) {
    return <FullPageSpinnerWithText text="Loading agent details" />
  }
  if (isError || !data) {
    return (
      <div role="alert" className="p-4 text-failed">
        Could not load agent {agentId}.
      </div>
    )
  }

  const isGraph = data.agent_model.model_type === "Graph"

  return (
    // `select-text` re-opts this page out of the app-wide
    // `body { user-select: none }` (global.css): ids, schemas and
    // prompts on this page are reference data the user copies.
    <div className="container mx-auto space-y-3.5 py-6 select-text">
      <AgentHeader agent={data} />

      {isGraph ? null : (
        // Evolver / meta agents (no graph) get the standalone Lineage
        // card under the new header — canonical route links for the
        // lineage ids, the agent's fitness, and (for evolver / meta
        // roles) the descendants the agent produced (US §17, M5 hook).
        <LineageCard agent={data} />
      )}

      {/* Keyed by the agent so page-local card state (selection, render
          failure, fullscreen) resets when navigating between cached
          agents without a loading unmount. */}
      <AgentModelRenderer key={data.agent_id} model={data.agent_model} />

      {data.agent_model.model_type === "Graph" ? (
        <BottomCards agent={data} />
      ) : null}
    </div>
  )
}
